import React, { useEffect, useRef, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { useExtendedMutation } from 'mutations';
import { fetchCards, deleteCard, addCard } from 'api';
import { Spinner } from "components";
import { useAuth } from "react-oidc-context";
import './WordListPage.css';

const WordListPage = () => {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 768);
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingWord, setDeletingWord] = useState(null);
  const [updatingWord, setUpdatingWord] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { rowWord, field, value }
  const pendingUpdate = useRef(null);

  const getCardsSuccessCallback = (data) => {
    setRowData(data);
    setIsLoading(false);
  }

  const getCardsErrorCallback = (error) => {
    console.error('Error submitting data:', error);
    setRowData([
      { userId: 'Bogdan', score: "4", word: "dummy dummy dummy dummy dummy dummy dummy dummy dummy dummy dummy dummy dummy dummy dummy dummy", definition: "dummyDef" },
      { userId: 'Bogdan', score: "5", word: "dummydummy", definition: "dummyDef3" }
    ]);
    setIsLoading(false);
  }

  const deleteCardSuccessCallback = () => {
    if (pendingUpdate.current) {
      const { word, definition, user, score } = pendingUpdate.current;
      pendingUpdate.current = null;
      addCardMutation.mutateOnVirtualThread({ word, definition, user, score, token });
    } else {
      setDeletingWord(null);
      getCardsMutation.mutateOnVirtualThread({ token });
    }
  }

  const deleteCardErrorCallback = (error) => {
    console.error('Error deleting data:', error);
    setDeletingWord(null);
    setUpdatingWord(null);
    pendingUpdate.current = null;
  }

  const addCardSuccessCallback = () => {
    setUpdatingWord(null);
    getCardsMutation.mutateOnVirtualThread({ token });
  }

  const addCardErrorCallback = (error) => {
    console.error('Error updating card:', error);
    setUpdatingWord(null);
  }

  const getCardsMutation = useExtendedMutation(fetchCards, getCardsSuccessCallback, getCardsErrorCallback);
  const deleteCardMutation = useExtendedMutation(deleteCard, deleteCardSuccessCallback, deleteCardErrorCallback);
  const addCardMutation = useExtendedMutation(addCard, addCardSuccessCallback, addCardErrorCallback);

  useEffect(() => {
    getCardsMutation.mutateOnVirtualThread({ token });
    const handleResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDelete = (word) => {
    setDeletingWord(word);
    deleteCardMutation.mutateOnVirtualThread({ word, token });
  };

  const startEdit = (row, field) => {
    if (deletingWord || updatingWord) return;
    setEditingCell({ rowWord: row.word, field, value: row[field] });
  };

  const handleEditSave = () => {
    if (!editingCell) return;
    const originalRow = rowData.find(r => r.word === editingCell.rowWord);
    if (!originalRow) { setEditingCell(null); return; }

    const newWord = editingCell.field === 'word' ? editingCell.value.trim() : originalRow.word;
    const newDefinition = editingCell.field === 'definition' ? editingCell.value.trim() : originalRow.definition;

    setEditingCell(null);

    if (!newWord || (newWord === originalRow.word && newDefinition === originalRow.definition)) return;

    setUpdatingWord(originalRow.word);
    pendingUpdate.current = { word: newWord, definition: newDefinition, user: originalRow.userId, score: originalRow.score };
    deleteCardMutation.mutateOnVirtualThread({ word: originalRow.word, token });
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') handleEditSave();
    if (e.key === 'Escape') setEditingCell(null);
  };

  const renderCell = (row, field, className) => {
    const isEditing = editingCell?.rowWord === row.word && editingCell?.field === field;
    if (isEditing) {
      return (
        <input
          className="word-list-edit-input"
          value={editingCell.value}
          autoFocus
          maxLength={field === 'word' ? 35 : 100}
          onChange={e => setEditingCell(prev => ({ ...prev, value: e.target.value }))}
          onBlur={handleEditSave}
          onKeyDown={handleEditKeyDown}
        />
      );
    }
    return (
      <span className={`${className} editable-cell`} onClick={() => startEdit(row, field)}>
        {row[field]}
      </span>
    );
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="word-list-background">
      <div className="word-list-container">
        <table className="word-list-table">
          <thead>
            <tr>
              {isNarrow ? (
                <>
                  <th>Word / Definition</th>
                  <th></th>
                </>
              ) : (
                <>
                  <th>Word</th>
                  <th>Definition</th>
                  <th></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rowData.map((row) => (
              <tr key={row.word}>
                {isNarrow ? (
                  <td>
                    <div>{renderCell(row, 'word', 'word-text')}</div>
                    <div>{renderCell(row, 'definition', 'definition-text')}</div>
                  </td>
                ) : (
                  <>
                    <td>{renderCell(row, 'word', 'word-text')}</td>
                    <td>{renderCell(row, 'definition', 'definition-text')}</td>
                  </>
                )}
                <td className="delete-cell">
                  {deletingWord === row.word || updatingWord === row.word
                    ? <img src="/lexiSpinner.png" alt="Loading..." className="delete-spinner" />
                    : <FaTrash onClick={() => handleDelete(row.word)} className="delete-icon" />
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { WordListPage };
